import type { RouterContext } from '@/app/routing/router-context';
// Straight from the model file, not `@pages/games`: that entry also re-exports the
// lazy-loaded games page, which would pull it into the home page's bundle.
import { ISSUE_ID_GAMES } from '@pages/games/model/games.hooks';
import { getSections, getFeed, ISSUE_ID_FEED } from './home.hooks';

/**
 * `starmagazine`'s home page has no single shared `issuesOptions`-style
 * factory to ensure -- it renders `IssueSections` (`useSections`) and two
 * independent `FeedIssue` widgets (`useFeed`, news + games). Reuses the
 * exact same `get*` fetch helpers `home.hooks.ts`'s hooks call (offline-
 * cache-fallback logic included), invoked directly against
 * `context.queryClient` with the identical queryKey shape each hook uses.
 *
 * Fire-and-forget, same reasoning as `app/routing/home-loaders.ts`'s
 * `homeIssuesLoader`: home renders real, data-independent UI (the login
 * gate, `Topbar`) regardless of whether this has resolved.
 */
export function starmagazineHomeLoader({ context }: { context: RouterContext }) {
	// Unmigrated: this template's `getSections`/`getFeed` take their own
	// required `isOnline`, with offline branches this pass hasn't audited --
	// unlike the shared `CacheArgs.isOnline`, which loaders now omit
	// entirely. Left reading the flag exactly as before rather than
	// half-migrating a fetcher without testing this tenant.
	const isOnline = navigator.onLine;
	const queryClient = context.queryClient;

	const sectionsKey = ['sections'];
	queryClient
		.ensureQueryData({
			queryKey: sectionsKey,
			queryFn: () => getSections({ queryClient, queryKey: sectionsKey, isOnline }),
		})
		.catch(() => {});

	const feeds: Array<{ id: string; limit: number }> = [
		{ id: `${ISSUE_ID_FEED}`, limit: 50 },
		{ id: `${ISSUE_ID_GAMES}`, limit: 10 },
	];
	for (const { id, limit } of feeds) {
		const feedKey = ['issue', id, { feed: true }];
		queryClient
			.ensureQueryData({
				queryKey: feedKey,
				queryFn: () =>
					getFeed({ queryClient, queryKey: feedKey, id, limit, isOnline }),
			})
			.catch(() => {});
	}
}
